import { CancelOutlined, CheckCircleOutlined } from '@mui/icons-material';
import { IconButton, List, ListItem, ListItemButton, ListItemText, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, LinkProps } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import {
  TsrctNegotiationCaseProcessStep,
  TsrctNegotiationCaseProcessStepStatus,
  TsrctNegotiationCaseProcessStepVariant,
} from '@/generated/gql/graphql';
import { ModuleHash, ModuleHashAction } from '~/modules/Module.types';
import { RouteId } from '~/routes';

const linkToByVariant: {
  [key in TsrctNegotiationCaseProcessStepVariant]: (props: {
    demandId: string;
    negotiationCaseId: string;
  }) => LinkProps['to'];
} = {
  [TsrctNegotiationCaseProcessStepVariant.CONTACT]: ({ negotiationCaseId }) => {
    return {
      pathname: RouteId.negotiationDetail(negotiationCaseId),
      hash: `${ModuleHash.CONTACT}/${ModuleHashAction.EDIT}`,
    };
  },
  [TsrctNegotiationCaseProcessStepVariant.QUOTE]: ({ negotiationCaseId }) => {
    return {
      pathname: RouteId.negotiationDetail(negotiationCaseId),
      hash: `${ModuleHash.QUOTE}/${ModuleHashAction.EDIT}`,
    };
  },
  [TsrctNegotiationCaseProcessStepVariant.DEMAND_REQUEST]: ({ demandId }) => {
    return {
      pathname: RouteId.demandDetail(demandId),
      hash: `${ModuleHash.DEMAND_REQUEST}/${ModuleHashAction.EDIT}`,
    };
  },
  [TsrctNegotiationCaseProcessStepVariant.COMPETITIVE_BIDDING_DEADLINE]: ({ demandId }) => {
    return {
      pathname: RouteId.demandDetail(demandId),
    };
  },
  [TsrctNegotiationCaseProcessStepVariant.AWARDING_DEADLINE]: ({ demandId }) => {
    return {
      pathname: RouteId.demandDetail(demandId),
    };
  },
  [TsrctNegotiationCaseProcessStepVariant.QUOTE_ITEMS_REQUESTED]: ({ negotiationCaseId }) => {
    return {
      pathname: RouteId.negotiationDetail(negotiationCaseId),
      hash: `${ModuleHash.QUOTE}/${ModuleHashAction.EDIT}`,
    };
  },
  [TsrctNegotiationCaseProcessStepVariant.DEMAND_RELEASABLE]: ({ demandId }) => {
    return {
      pathname: RouteId.demandDetail(demandId),
    };
  },
};

export interface NegotiationCaseIsReleasableCellMenuProps {
  list: Union.Nullable<TsrctNegotiationCaseProcessStep[]>;
  negotiationCaseId: string;
  demandId: string;
}

export const NegotiationCaseIsReleasableCellMenu = ({
  list,
  negotiationCaseId,
  demandId,
}: NegotiationCaseIsReleasableCellMenuProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationCaseActionVariant' });
  return (
    <List disablePadding>
      {list &&
        list.map((item, index: number) => {
          const to = linkToByVariant[item.variant]({ negotiationCaseId, demandId });
          return (
            <ListItem
              disablePadding
              key={index}
              secondaryAction={
                <Stack direction="row" spacing={1}>
                  <IconButton edge="end" disabled={true}>
                    {item.status === TsrctNegotiationCaseProcessStepStatus.COMPLETED ? (
                      <CheckCircleOutlined color="success" />
                    ) : (
                      <CancelOutlined color="error" />
                    )}
                  </IconButton>
                </Stack>
              }
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemButton component={RouterLink} to={to} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                <ListItemText primary={t(item.variant)} />
              </ListItemButton>
            </ListItem>
          );
        })}
    </List>
  );
};
